import React, { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import { TIMBRATURA, APP } from "@/constants/testIds";
import SignaturePad from "@/components/SignaturePad";
import { toast } from "sonner";
import { MapPin, LogIn, LogOut, Coffee, Play, AlertTriangle, Loader2, Trash2, Hammer, Wrench, HardHat, Zap } from "lucide-react";

const LAVORAZIONI_USCITA = [
  { key: "approntamento", label: "Approntamento", icon: HardHat, color: "bg-slate-700 border-slate-800" },
  { key: "demolizioni", label: "Demolizioni", icon: Hammer, color: "bg-orange-600 border-orange-700" },
  { key: "costruzioni", label: "Costruzioni", icon: Wrench, color: "bg-blue-700 border-blue-800" },
  { key: "impianto_elettrico", label: "Imp. E", icon: Zap, color: "bg-amber-600 border-amber-700" },
  { key: "impianto_idraulico", label: "Imp. I", icon: Wrench, color: "bg-cyan-700 border-cyan-800" },
  { key: "impianto_condizionamento", label: "Imp. Cond.", icon: Zap, color: "bg-indigo-700 border-indigo-800" },
  { key: "tinteggiature", label: "Tinteggiature", icon: HardHat, color: "bg-emerald-700 border-emerald-800" },
];

const TIPI = [
  { key: "entrata", label: "Entrata", icon: LogIn, testId: TIMBRATURA.tipoEntrata, color: "bg-emerald-600" },
  { key: "uscita", label: "Uscita", icon: LogOut, testId: TIMBRATURA.tipoUscita, color: "bg-slate-900" },
  { key: "inizio_pausa", label: "Inizio pausa", icon: Coffee, testId: TIMBRATURA.tipoInizioPausa, color: "bg-yellow-600" },
  { key: "fine_pausa", label: "Fine pausa", icon: Play, testId: TIMBRATURA.tipoFinePausa, color: "bg-blue-600" },
];

export default function Timbratura() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const sigRef = useRef(null);

  const [tipo, setTipo] = useState("entrata");
  const [cantiereId, setCantiereId] = useState("");
  const [lavoratoreId, setLavoratoreId] = useState("");
  const [nota, setNota] = useState("");
  const [motivazione, setMotivazione] = useState("");
  const [lavorazioneKey, setLavorazioneKey] = useState(""); // solo per uscita (retrocompat)
  const [lavorazioni, setLavorazioni] = useState([]); // [{key, percentuale}] max 2 elementi
  const [reportAnno, setReportAnno] = useState(new Date().getFullYear());
  const [reportMese, setReportMese] = useState(new Date().getMonth() + 1);
  const [coords, setCoords] = useState(null);
  const [loading, setLoading] = useState(false);
  const [firma, setFirma] = useState(null);
  const [entrataManualeId, setEntrataManualeId] = useState("");
  const [uscitaManualeDataOra, setUscitaManualeDataOra] = useState("");
  const [uscitaManualeMotivo, setUscitaManualeMotivo] = useState("");


  const { data: cantieri = [] } = useQuery({
    queryKey: ["cantieri-attivi"],
    // Timbratura shows ALL active cantieri to any authenticated user
    // (workers may need to punch on any active jobsite, not just pre-assigned ones).
    queryFn: () => api.get("/cantieri/attivi").then((r) => r.data),
  });
  const { data: lavoratori = [] } = useQuery({
    queryKey: ["lavoratori"],
    queryFn: () => api.get("/lavoratori").then((r) => r.data),
  });
  // For non-admin users: try to auto-select their linked Lavoratore
  const { data: myLavoratore } = useQuery({
    queryKey: ["lavoratore-me"],
    queryFn: () => api.get("/lavoratori/me").then((r) => r.data),
    retry: false,
  });
  const { data: recent = [] } = useQuery({
    queryKey: ["timbrature-recent"],
    queryFn: () => api.get("/timbrature").then((r) => r.data),
  });

  useEffect(() => {
    if (!cantiereId && cantieri[0]) setCantiereId(cantieri[0].cantiere_id);
  }, [cantieri, cantiereId, setCantiereId]);
  useEffect(() => {
    // Preselect linked lavoratore if available, otherwise first of the list
    if (!lavoratoreId) {
      if (myLavoratore?.lavoratore_id) {
        setLavoratoreId(myLavoratore.lavoratore_id);
      } else if (lavoratori[0]) {
        setLavoratoreId(lavoratori[0].lavoratore_id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lavoratori, lavoratoreId, myLavoratore, setLavoratoreId]);

  const cantiere = cantieri.find((c) => c.cantiere_id === cantiereId);
  const distanza =
    coords && cantiere?.lat && cantiere?.lng
      ? haversine(coords.lat, coords.lng, cantiere.lat, cantiere.lng)
      : null;
  const fuoriRaggio = distanza !== null && cantiere && distanza > (cantiere.raggio_metri || 200);

  const getGPS = () => {
    setLoading(true);
    if (!navigator.geolocation) {
      toast.error("Geolocalizzazione non disponibile");
      setLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLoading(false);
      },
      (err) => {
        toast.error("Impossibile ottenere posizione: " + err.message);
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const submit = useMutation({
    mutationFn: (payload) => api.post("/timbrature", payload).then((r) => r.data),
    onSuccess: (data) => {
      // Se l'uscita ha prodotto un avanzamento del cronoprogramma, mostra il dettaglio
      if (data?.avanzamento_attivita_id && data?.avanzamento_ore) {
        toast.success(`Uscita registrata · ${data.avanzamento_ore}h attribuite a "${data.avanzamento_attivita_nome}"`);
      } else {
        toast.success("Timbratura registrata");
      }
      qc.invalidateQueries({ queryKey: ["timbrature-recent"] });
      qc.invalidateQueries({ queryKey: ["attivita-crono"] });
      qc.invalidateQueries({ queryKey: ["programma-lavori-overview"] });
      sigRef.current?.clear();
      setFirma(null);
      setNota("");
      setMotivazione("");
      setLavorazioneKey("");
      setLavorazioni([]);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const canSubmit = cantiereId && lavoratoreId && tipo;
  const isAdmin = user?.role === "amministratore";

  const deleteTimbratura = useMutation({
    mutationFn: (id) => api.delete(`/timbrature/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success("Presenza eliminata — riepiloghi e report ricalcolati");
      // Le timbrature sono la source-of-truth: invalido tutte le query che le usano
      qc.invalidateQueries({ queryKey: ["timbrature-recent"] });
      qc.invalidateQueries({ queryKey: ["timbrature"] });
      qc.invalidateQueries({ queryKey: ["presenze-cantiere"] });
      qc.invalidateQueries({ queryKey: ["presenze"] });
      qc.invalidateQueries({ queryKey: ["report-presenze"] });
      qc.invalidateQueries({ queryKey: ["ore-lavorate"] });
      qc.invalidateQueries({ queryKey: ["riepilogo-mensile"] });
      qc.invalidateQueries({ queryKey: ["dashboard-timbrature"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Eliminazione fallita"),
  });

  const entrateManuali = recent.filter((t) => t.tipo === "entrata");
  const entrataSelezionata = entrateManuali.find(
    (t) => t.timbratura_id === entrataManualeId
  );

  const inserisciUscitaManuale = useMutation({
    mutationFn: (payload) =>
      api.post("/timbrature/admin/uscita-manuale", payload).then((r) => r.data),
    onSuccess: () => {
      toast.success("Uscita amministrativa registrata");
      setEntrataManualeId("");
      setUscitaManualeDataOra("");
      setUscitaManualeMotivo("");
      qc.invalidateQueries({ queryKey: ["timbrature-recent"] });
      qc.invalidateQueries({ queryKey: ["timbrature"] });
      qc.invalidateQueries({ queryKey: ["report-ore"] });
      qc.invalidateQueries({ queryKey: ["ore-lavorate"] });
      qc.invalidateQueries({ queryKey: ["riepilogo-mensile"] });
      qc.invalidateQueries({ queryKey: ["report-presenze"] });
      qc.invalidateQueries({ queryKey: ["dashboard-timbrature"] });
    },
    onError: (e) =>
      toast.error(e.response?.data?.detail || "Inserimento uscita non riuscito"),
  });

  const confermaUscitaManuale = () => {
    if (!entrataSelezionata || !uscitaManualeDataOra || !uscitaManualeMotivo.trim()) {
      return toast.error("Seleziona entrata, data e ora di uscita e motivazione");
    }
    const timestamp = new Date(uscitaManualeDataOra);
    if (Number.isNaN(timestamp.getTime())) {
      return toast.error("Data e ora non valide");
    }
    if (!window.confirm(
      `Confermi l'uscita amministrativa per ${entrataSelezionata.lavoratore_nome || "il lavoratore"}?\\n` +
      `Cantiere: ${entrataSelezionata.cantiere_nome || entrataSelezionata.cantiere_id}\\n` +
      `Uscita: ${timestamp.toLocaleString("it-IT")}\\n\\n` +
      "L'operazione sara registrata con la tua identita e la motivazione."
    )) return;

    inserisciUscitaManuale.mutate({
      entrata_id: entrataManualeId,
      timestamp_uscita: timestamp.toISOString(),
      motivazione: uscitaManualeMotivo.trim(),
    });
  };

  const handleSubmit = () => {
    if (!canSubmit) return toast.error("Compila cantiere e lavoratore");
    if (tipo === "uscita" && lavorazioni.length === 0) {
      return toast.error("Seleziona la lavorazione svolta oggi (una o due) prima di timbrare l'uscita.");
    }
    submit.mutate({
      lavoratore_id: lavoratoreId,
      cantiere_id: cantiereId,
      tipo,
      lat: coords?.lat,
      lng: coords?.lng,
      dispositivo: navigator.userAgent.slice(0, 100),
      firma_base64: firma,
      nota,
      motivazione_fuori_raggio: motivazione,
      lavorazioni: tipo === "uscita" ? lavorazioni : undefined,
      lavorazione_key: tipo === "uscita" && lavorazioni.length === 1 ? lavorazioni[0].key : undefined,
    });
  };

  // Toggle lavorazione: aggiunge o rimuove dalla selezione (max 2)
  const toggleLavorazione = (key) => {
    setLavorazioni((prev) => {
      const idx = prev.findIndex((l) => l.key === key);
      if (idx >= 0) {
        // rimuovi
        const next = prev.filter((_, i) => i !== idx);
        if (next.length === 1) return [{ ...next[0], percentuale: 100 }];
        return next;
      }
      if (prev.length >= 2) {
        toast.error("Puoi selezionare al massimo 2 lavorazioni");
        return prev;
      }
      if (prev.length === 0) return [{ key, percentuale: 100 }];
      // aggiungi seconda con split 50/50
      return [{ ...prev[0], percentuale: 50 }, { key, percentuale: 50 }];
    });
  };

  // Slider % prima lavorazione: la seconda è complementare a 100
  const setPercentualePrima = (val) => {
    setLavorazioni((prev) => {
      if (prev.length !== 2) return prev;
      const v = Math.max(10, Math.min(90, parseInt(val, 10) || 50));
      return [{ ...prev[0], percentuale: v }, { ...prev[1], percentuale: 100 - v }];
    });
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <div className="overline">Timbratura</div>
        <h1 className="font-display text-3xl xl:text-4xl font-black">Registra presenza</h1>
        <p className="text-slate-600 text-sm mt-1">La posizione GPS viene acquisita solo al momento della timbratura.</p>
      </div>

      {/* Tipo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {TIPI.map((t) => (
          <button
            key={t.key}
            data-testid={t.testId}
            onClick={() => setTipo(t.key)}
            className={`flex flex-col items-center gap-2 p-5 border rounded-sm transition-all ${
              tipo === t.key
                ? `${t.color} text-white border-transparent shadow-brutal`
                : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"
            }`}
          >
            <t.icon className="w-6 h-6" />
            <span className="font-semibold text-sm">{t.label}</span>
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 p-5 space-y-4">
        {(cantieri.length === 0 || lavoratori.length === 0) && (
          <div className="p-4 border border-amber-300 bg-amber-50 rounded-sm text-sm text-amber-900" data-testid="timbratura-empty-state">
            <div className="font-semibold mb-1">⚠️ Configurazione necessaria</div>
            {cantieri.length === 0 && (
              <div>• Nessun cantiere attivo. Chiedi all'amministratore di creare un cantiere dalla pagina <b>Cantieri</b>.</div>
            )}
            {lavoratori.length === 0 && (
              <div>• Nessun lavoratore registrato. Chiedi all'amministratore di aggiungere i lavoratori dalla pagina <b>Lavoratori</b>.</div>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Cantiere</label>
            <select
              value={cantiereId}
              onChange={(e) => setCantiereId(e.target.value)}
              data-testid={TIMBRATURA.selectCantiere}
              disabled={cantieri.length === 0}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm disabled:bg-slate-100 disabled:text-slate-400"
            >
              {cantieri.length === 0 && <option value="">— Nessun cantiere disponibile —</option>}
              {cantieri.map((c) => (
                <option key={c.cantiere_id} value={c.cantiere_id}>{c.nome}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Lavoratore</label>
            <select
              value={lavoratoreId}
              onChange={(e) => setLavoratoreId(e.target.value)}
              data-testid={TIMBRATURA.selectLavoratore}
              disabled={lavoratori.length === 0}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm disabled:bg-slate-100 disabled:text-slate-400"
            >
              {lavoratori.length === 0 && <option value="">— Nessun lavoratore registrato —</option>}
              {lavoratori.map((l) => (
                <option key={l.lavoratore_id} value={l.lavoratore_id}>
                  {l.nome} {l.cognome} — {l.mansione}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* GPS */}
        <div className="border border-slate-200 p-4 rounded-sm bg-slate-50">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="overline mb-1">Posizione GPS</div>
              {coords ? (
                <div className="text-sm font-mono">
                  {coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}
                  {distanza !== null && (
                    <span className={`ml-3 font-bold ${fuoriRaggio ? "text-red-600" : "text-emerald-600"}`}>
                      · Distanza cantiere: {Math.round(distanza)} m
                    </span>
                  )}
                </div>
              ) : (
                <div className="text-sm text-slate-500">Non acquisita</div>
              )}
            </div>
            <button
              onClick={getGPS}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 border border-slate-900 hover:bg-slate-900 hover:text-white text-sm font-semibold rounded-sm transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
              Ottieni GPS
            </button>
          </div>

          {fuoriRaggio && (
            <div className="mt-3 flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-sm text-red-900">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <div>
                <div className="font-semibold">Sei fuori dal raggio autorizzato ({cantiere.raggio_metri}m)</div>
                <div className="text-xs text-red-800">La timbratura verrà inviata per approvazione all'amministratore.</div>
              </div>
            </div>
          )}
          {fuoriRaggio && (
            <textarea
              value={motivazione}
              onChange={(e) => setMotivazione(e.target.value)}
              data-testid={TIMBRATURA.motivazione}
              placeholder="Motivazione obbligatoria (es. sopralluogo in ufficio, riunione fuori sede…)"
              rows={2}
              className="w-full mt-2 px-3 py-2 border border-red-300 rounded-sm text-sm"
            />
          )}
        </div>

        {/* Lavorazione svolta (SOLO uscita) — supporta 1 o 2 lavorazioni con split % */}
        {tipo === "uscita" && (
          <div className="border border-blue-300 bg-blue-50/40 p-4 rounded-sm space-y-2" data-testid="timbratura-lavorazione-panel">
            <div className="flex items-center justify-between">
              <div className="overline text-blue-800">Lavorazione svolta oggi *</div>
              {lavorazioni.length > 0 && (
                <button type="button" onClick={() => setLavorazioni([])} className="text-[10px] text-blue-700 hover:underline font-semibold">svuota</button>
              )}
            </div>
            <p className="text-[11px] text-slate-600">
              Seleziona 1 lavorazione (giornata intera) oppure 2 lavorazioni (giornata spezzata) — puoi decidere la percentuale di ore per ciascuna. Le ore verranno associate automaticamente al cronoprogramma.
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {LAVORAZIONI_USCITA.map((l) => {
                const idx = lavorazioni.findIndex((x) => x.key === l.key);
                const active = idx >= 0;
                const pct = active ? lavorazioni[idx].percentuale : null;
                return (
                  <button
                    key={l.key}
                    type="button"
                    onClick={() => toggleLavorazione(l.key)}
                    data-testid={`btn-lavorazione-${l.key}`}
                    className={`flex flex-col items-center gap-1 p-3 border-2 rounded-sm text-xs font-semibold transition-all relative ${
                      active
                        ? `${l.color} text-white shadow-brutal`
                        : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                    }`}
                  >
                    <l.icon className="w-5 h-5" />
                    <span className="text-center leading-tight">{l.label}</span>
                    {active && (
                      <span className="absolute top-1 right-1 bg-white/90 text-slate-900 rounded-sm px-1.5 py-0.5 text-[10px] font-black">{pct}%</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Slider quando ci sono 2 lavorazioni */}
            {lavorazioni.length === 2 && (
              <div className="mt-3 p-3 bg-white border border-blue-300 rounded-sm" data-testid="lavorazioni-split-slider">
                <div className="flex items-center justify-between text-[11px] font-semibold mb-1">
                  <span className="text-slate-700">
                    {LAVORAZIONI_USCITA.find((x) => x.key === lavorazioni[0].key)?.label}: <b>{lavorazioni[0].percentuale}%</b>
                  </span>
                  <span className="text-slate-700 text-right">
                    <b>{lavorazioni[1].percentuale}%</b> {LAVORAZIONI_USCITA.find((x) => x.key === lavorazioni[1].key)?.label}
                  </span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="90"
                  step="5"
                  value={lavorazioni[0].percentuale}
                  onChange={(e) => setPercentualePrima(e.target.value)}
                  data-testid="lavorazioni-split-range"
                  className="w-full accent-blue-600"
                />
                <div className="flex justify-between text-[9px] text-slate-500 mt-0.5">
                  <span>10%</span><span>50/50</span><span>90%</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Nota (opzionale)</label>
          <textarea
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            data-testid={TIMBRATURA.nota}
            rows={2}
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-semibold text-slate-600">Firma</label>
            <button
              onClick={() => { sigRef.current?.clear(); setFirma(null); }}
              data-testid={TIMBRATURA.clearSignature}
              className="text-xs text-slate-500 hover:text-slate-900"
            >
              Cancella
            </button>
          </div>
          <div data-testid={TIMBRATURA.signature}>
            <SignaturePad ref={sigRef} onChange={setFirma} />
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={submit.isPending}
          data-testid={TIMBRATURA.confirmButton}
          className="w-full py-4 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-semibold rounded-sm shadow-brutal"
        >
          {submit.isPending ? "Invio…" : `Conferma ${TIPI.find((t) => t.key === tipo)?.label}`}
        </button>
      </div>

      {/* Report Excel ore per lavorazione — admin only */}
      {isAdmin && (
        <div className="bg-white border border-slate-200 p-4 flex flex-wrap items-center gap-3" data-testid="report-ore-lavorazione-panel">
          <div>
            <div className="overline">Report ore per lavorazione</div>
            <div className="text-xs text-slate-500">Excel mensile pronto per busta paga</div>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <select value={reportMese} onChange={(e) => setReportMese(Number(e.target.value))} data-testid="report-mese" className="px-2 py-1.5 border border-slate-300 rounded-sm text-sm bg-white">
              {["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"].map((m, i) => (
                <option key={i} value={i + 1}>{m}</option>
              ))}
            </select>
            <input type="number" min="2020" max="2099" value={reportAnno} onChange={(e) => setReportAnno(Number(e.target.value))} data-testid="report-anno" className="w-20 px-2 py-1.5 border border-slate-300 rounded-sm text-sm" />
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/timbrature/report/ore-per-lavorazione.xlsx?anno=${reportAnno}&mese=${reportMese}`}
              target="_blank"
              rel="noreferrer"
              data-testid="btn-download-report-xlsx"
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold rounded-sm shadow-brutal"
            >
              ⬇ Excel
            </a>
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="bg-white border border-amber-300 p-4 space-y-3" data-testid="uscita-manuale-panel">
          <div>
            <div className="overline">Correzione amministrativa</div>
            <h2 className="font-semibold text-lg">Inserisci uscita mancante</h2>
            <p className="text-sm text-slate-600">
              Seleziona l'entrata originale. Il cantiere viene mantenuto automaticamente.
              Il sistema verifica eventuali timbrature incompatibili prima di salvare.
            </p>
          </div>

          <label className="block text-sm font-medium">
            Entrata da correggere
            <select
              value={entrataManualeId}
              onChange={(e) => setEntrataManualeId(e.target.value)}
              className="block w-full mt-1 border border-slate-300 p-2 bg-white"
            >
              <option value="">Seleziona una timbratura di entrata</option>
              {entrateManuali.map((t) => (
                <option key={t.timbratura_id} value={t.timbratura_id}>
                  {t.lavoratore_nome || t.lavoratore_id} — {new Date(t.timestamp).toLocaleString("it-IT")} — {t.cantiere_nome || t.cantiere_id}
                </option>
              ))}
            </select>
          </label>

          {entrataSelezionata && (
            <p className="text-sm text-slate-700">
              Cantiere: <strong>{entrataSelezionata.cantiere_nome || entrataSelezionata.cantiere_id}</strong>
            </p>
          )}

          <label className="block text-sm font-medium">
            Data e ora effettiva di uscita (ora italiana)
            <input
              type="datetime-local"
              value={uscitaManualeDataOra}
              onChange={(e) => setUscitaManualeDataOra(e.target.value)}
              className="block w-full mt-1 border border-slate-300 p-2"
            />
          </label>

          <label className="block text-sm font-medium">
            Motivazione della correzione
            <textarea
              value={uscitaManualeMotivo}
              onChange={(e) => setUscitaManualeMotivo(e.target.value)}
              rows={2}
              placeholder="Es. Uscita dimenticata, verificata con il responsabile"
              className="block w-full mt-1 border border-slate-300 p-2"
            />
          </label>

          <button
            type="button"
            onClick={confermaUscitaManuale}
            disabled={inserisciUscitaManuale.isPending}
            className="px-4 py-2 bg-slate-900 text-white font-semibold disabled:opacity-50"
          >
            {inserisciUscitaManuale.isPending ? "Registrazione..." : "Registra uscita mancante"}
          </button>
        </div>
      )}

      {/* Recent */}
      <div className="bg-white border border-slate-200">
        <div className="p-4 border-b border-slate-200"><div className="overline">Timbrature recenti</div></div>
        <div className="divide-y divide-slate-100">
          {recent.slice(0, 10).map((t) => (
            <div key={t.timbratura_id} className="p-3 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`timbratura-row-${t.timbratura_id}`}>
              <div>
                <div className="font-medium">{t.lavoratore_nome}</div>
                <div className="text-xs text-slate-500">{t.cantiere_nome}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-sm">{t.tipo.replace('_',' ')}</span>
                <span className="font-mono text-xs">{new Date(t.timestamp).toLocaleString("it-IT")}</span>
                {t.fuori_raggio && (
                  <span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-red-100 text-red-800 border border-red-200 rounded-sm">Fuori raggio</span>
                )}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => {
                      const label = `${t.lavoratore_nome} · ${t.tipo.replace('_',' ')} · ${new Date(t.timestamp).toLocaleString("it-IT")}`;
                      if (window.confirm(`Eliminare definitivamente questa presenza?\n\n${label}\n\nLa presenza verrà rimossa completamente e i totali (ore lavorate, ordinarie/straordinarie, pause, giornaliero/settimanale/mensile) verranno ricalcolati automaticamente.\n\nQuesta azione NON è reversibile.`)) {
                        deleteTimbratura.mutate(t.timbratura_id);
                      }
                    }}
                    disabled={deleteTimbratura.isPending}
                    title="Elimina presenza (solo admin)"
                    data-testid={`btn-delete-timbratura-${t.timbratura_id}`}
                    className="p-1.5 text-slate-400 hover:text-rose-700 hover:bg-rose-50 rounded-sm disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {recent.length === 0 && <div className="p-6 text-slate-400 text-sm text-center">Nessuna timbratura recente</div>}
        </div>
      </div>
    </div>
  );
}

function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = (x) => (x * Math.PI) / 180;
  const dp = toRad(lat2 - lat1);
  const dl = toRad(lon2 - lon1);
  const a = Math.sin(dp / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dl / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
